import React, { useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import { toast } from "react-toastify";
import { PlusCircleOutlined } from "../../../../helpers/icons/antDesignIcons";
import useTranslation from "../../../../hooks/useTranslation";
import "./AddNewCompanyModal.scss";
import { addNewCompany } from "../../../../api/settingsCompanyApi";

// Validation function (Relaxed: Only Company Name is required)
const validate = (values, t) => {
  const errors = {};

  // 1. Company Name Validation (REQUIRED)
  if (!values.companyName || values.companyName.trim() === "") {
    errors.companyName = t("add_new_company_error_field_empty");
  } else if (values.companyName.length < 3 || values.companyName.length > 100) {
    errors.companyName = t("add_new_company_error_companyname");
  }

  // 2. Optional Fields (Only validates format IF the user typed something)
  if (values.companyCity && (values.companyCity.length < 3 || values.companyCity.length > 70)) {
    errors.companyCity = t("add_new_company_error_city");
  }

  if (values.companyZip && !/^\d{4,5}$/.test(values.companyZip)) {
    errors.companyZip = t("add_new_company_error_zip");
  }

  if (values.companyStreet && (values.companyStreet.length < 3 || values.companyStreet.length > 50)) {
    errors.companyStreet = t("add_new_company_error_street");
  }

  if (values.companyHouseNr && values.companyHouseNr.length > 20) {
    errors.companyHouseNr = t("add_new_company_error_housenumber");
  }

  if (values.companyTaxNr && !/^[0-9]{8}-[0-9]{1}-[0-9]{2}$/.test(values.companyTaxNr)) {
    errors.companyTaxNr = t("add_new_company_error_taxnumber_invalid");
  }

  if (values.companyEmail && !/\S+@\S+\.\S+/.test(values.companyEmail)) {
    errors.companyEmail = t("add_new_company_error_email_invalid");
  } else if (values.companyEmail && (values.companyEmail.length < 3 || values.companyEmail.length > 100)) {
    errors.companyEmail = t("add_new_company_error_email_length");
  }

  if (values.companyPhone && !/^\+?[0-9]{7,15}$/.test(values.companyPhone)) {
    errors.companyPhone = "A telefonszám érvénytelen. Példa: +36301234567.";
  }

  if (values.companyWebsite && !/^https?:\/\/[^\s$.?#].[^\s]*$/.test(values.companyWebsite)) {
    errors.companyWebsite = "Az URL érvénytelen. Példa: https://minta.hu";
  }

  return errors;
};

// Function to map the data to the backend format
const mapToBackendFormat = (data) => ({
  companyName: data.companyName,
  companyCity: data.companyCity,
  zip: data.companyZip,
  street: data.companyStreet,
  houseNr: data.companyHouseNr,
  taxNumber: data.companyTaxNr,
  companyEmail: data.companyEmail,
  phoneNumber: data.companyPhone,
  website: data.companyWebsite,
  status: data.companyStatus === "true",
  licences: parseInt(data.companyLicense, 10) || 0,
  shrinkage_control: data.companyShrinkageControl === "true", // Convert to boolean for backend
});

const AddNewCompanyModal = () => {
  const { t } = useTranslation("settingsPage", true);
  const [errors, setErrors] = useState({});
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [companyName, setCompanyName] = useState("");
  const [companyCity, setCompanyCity] = useState("");
  const [companyZip, setCompanyZip] = useState("");
  const [companyStreet, setCompanyStreet] = useState("");
  const [companyHouseNr, setCompanyHouseNr] = useState("");
  const [companyTaxNr, setCompanyTaxNr] = useState("");
  const [companyEmail, setCompanyEmail] = useState("");
  const [companyPhone, setCompanyPhone] = useState("");
  const [companyWebsite, setCompanyWebsite] = useState("");
  const [companyStatus, setCompanyStatus] = useState("true");
  const [companyLicense, setCompanyLicense] = useState("");
  const [companyShrinkageControl, setCompanyShrinkageControl] = useState("false");

  const handleAddNewCompanyClick = () => {
    setModalIsOpen(true);
  };

  const handleClose = () => {
    setModalIsOpen(false);
    setErrors({});
  };

  const handleSave = async () => {
    const validationErrors = validate(
      {
        companyName,
        companyCity,
        companyZip,
        companyStreet,
        companyHouseNr,
        companyTaxNr,
        companyEmail,
        companyPhone,
        companyWebsite,
      },
      t
    );

    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      return; // Stop execution if there are validation errors
    }

    const addNewCompanyDataForBackend = mapToBackendFormat({
      companyName,
      companyCity,
      companyZip,
      companyStreet,
      companyHouseNr,
      companyTaxNr,
      companyEmail,
      companyPhone,
      companyWebsite,
      companyStatus,
      companyLicense,
      companyShrinkageControl,
    });

    try {
      const response = await addNewCompany(addNewCompanyDataForBackend);

      if (response.success) {
        toast.success(t("add_new_company_success"));

        setModalIsOpen(false);
        setCompanyName("");
        setCompanyCity("");
        setCompanyZip("");
        setCompanyStreet("");
        setCompanyHouseNr("");
        setCompanyTaxNr("");
        setCompanyEmail("");
        setCompanyPhone("");
        setCompanyWebsite("");
        setCompanyStatus("true");
        setCompanyLicense("");
        setCompanyShrinkageControl("false");
        setErrors({});
      } else {
        toast.error(t("add_new_company_error"));
      }
    } catch (error) {
      if (error.message === "Company with the same data already exists.") {
        toast.error(t("add_new_company_error_duplicate"));
      } else {
        toast.error(t("add_new_company_error"));
      }
      console.error("Error saving company data:", error.message);
    }
  };

  return (
    <>
      <div className="anc__add-button__container">
        <Button className="anc__add-button" onClick={handleAddNewCompanyClick}>
          <PlusCircleOutlined /> {t("add_new_company")}
        </Button>
      </div>
      <div className="anc__modal__container">
        <Modal
          centered
          show={modalIsOpen}
          onHide={handleClose}
          className="anc__modal"
          size="lg"
        >
          <Modal.Header closeButton>
            <Modal.Title>{t("add_new_company")}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <form className="anc__form">
              {/* REQUIRED FIELD */}
              <Form.Group className="anc-form-item">
                <Form.Label>{t("company_name")}*</Form.Label>
                <Form.Control
                  type="text"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  required
                  isInvalid={!!errors.companyName}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.companyName}
                </Form.Control.Feedback>
              </Form.Group>

              {/* OPTIONAL FIELDS */}
              <Form.Group className="anc-form-item">
                <Form.Label>{t("company_city")}</Form.Label>
                <Form.Control
                  type="text"
                  value={companyCity}
                  onChange={(e) => setCompanyCity(e.target.value)}
                  isInvalid={!!errors.companyCity}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.companyCity}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="anc-form-item">
                <Form.Label>{t("company_zip")}</Form.Label>
                <Form.Control
                  type="text"
                  value={companyZip}
                  onChange={(e) => setCompanyZip(e.target.value)}
                  isInvalid={!!errors.companyZip}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.companyZip}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="anc-form-item">
                <Form.Label>{t("company_street")}</Form.Label>
                <Form.Control
                  type="text"
                  value={companyStreet}
                  onChange={(e) => setCompanyStreet(e.target.value)}
                  isInvalid={!!errors.companyStreet}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.companyStreet}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="anc-form-item">
                <Form.Label>{t("company_house_number")}</Form.Label>
                <Form.Control
                  type="text"
                  value={companyHouseNr}
                  onChange={(e) => setCompanyHouseNr(e.target.value)}
                  isInvalid={!!errors.companyHouseNr}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.companyHouseNr}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="anc-form-item">
                <Form.Label>{t("company_tax_number")}</Form.Label>
                <Form.Control
                  type="text"
                  value={companyTaxNr}
                  onChange={(e) => setCompanyTaxNr(e.target.value)}
                  isInvalid={!!errors.companyTaxNr}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.companyTaxNr}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="anc-form-item">
                <Form.Label>{t("email")}</Form.Label>
                <Form.Control
                  type="email"
                  value={companyEmail}
                  onChange={(e) => setCompanyEmail(e.target.value)}
                  isInvalid={!!errors.companyEmail}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.companyEmail}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="anc-form-item">
                <Form.Label>{t("phone")}</Form.Label>
                <Form.Control
                  type="tel"
                  value={companyPhone}
                  onChange={(e) => setCompanyPhone(e.target.value)}
                  isInvalid={!!errors.companyPhone}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.companyPhone}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="anc-form-item">
                <Form.Label>{t("website")}</Form.Label>
                <Form.Control
                  type="url"
                  value={companyWebsite}
                  onChange={(e) => setCompanyWebsite(e.target.value)}
                  isInvalid={!!errors.companyWebsite}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.companyWebsite}
                </Form.Control.Feedback>
              </Form.Group>

              {/* REQUIRED STATUS & SHRINKAGE FIELDS */}
              <Form.Group className="anc-form-item">
                <Form.Label>{t("status")}*</Form.Label>
                <Form.Control
                  as="select"
                  value={companyStatus}
                  onChange={(e) => setCompanyStatus(e.target.value)}
                  required
                  isInvalid={!!errors.companyStatus}
                >
                  <option value="true">{t("active")}</option>
                  <option value="false">{t("inactive")}</option>
                </Form.Control>
                <Form.Control.Feedback type="invalid">
                  {errors.companyStatus}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="anc-form-item">
                <Form.Label>{t("shrinkage_control") || "Extra gyakori riasztások"}*</Form.Label>
                <Form.Control
                  as="select"
                  value={companyShrinkageControl}
                  onChange={(e) => setCompanyShrinkageControl(e.target.value)}
                  required
                >
                  <option value="true">{t("active") || "Aktív"}</option>
                  <option value="false">{t("inactive") || "Inaktív"}</option>
                </Form.Control>
              </Form.Group>
            </form>
            <div className="anc__modal__description">
              <p className="anc__modal__description__text">
                {t("starSignedFields")}
              </p>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleClose}>
              {t("cancel")}
            </Button>
            <Button variant="primary" onClick={handleSave}>
              {t("add")}
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </>
  );
};

export default AddNewCompanyModal;